import {
	Controller,
	useFieldArray,
	UseFieldArrayAppend,
	UseFieldArrayRemove,
	useFormContext,
	useWatch,
} from "react-hook-form";
import { intl } from "LocaleWrapper";

import { Button, FormGroup } from "@cofano/cck";
import { Box, Grid2 as Grid, Tooltip, Typography } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import { FormArrayContainer, FormArrayRow } from "components/cck/FormArray";
import NumberInput from "components/cck/NumberInput";

import ShipTankPropertiesForm from "view/ships/components/form/ShipTankPropertiesForm";
import { ShipSchemaInput, ShipSchemaOutput } from "view/ships/utils/ship.schema";

import { ChildProps, ChildPropsFromArray } from "types/commons";
import { TankConfiguration, TankNumberingScheme, TankPosition } from "__generated__/enums";
import { GetShipForFormQuery } from "__generated__/view/ships/graphql/ship.queries.generated";

type ShipFromQuery = ChildProps<GetShipForFormQuery, "ship">
type ShipSchemaTankInput = ChildPropsFromArray<ShipSchemaInput, "tanks">;

type ShipTanksProps = {
	ship?: Pick<ShipFromQuery, "id" | "tanks" | "tankProperties">;
	loading: boolean;
}

type ShipTanksLayoutProps = {
	tanks: ShipSchemaTankInput[];
	position: TankPosition;
	numberingScheme: TankNumberingScheme;
	append: UseFieldArrayAppend<ShipSchemaInput, "tanks">;
	remove: UseFieldArrayRemove;
}

type ShipTanksGeneralProps = ShipTanksLayoutProps & {
	defaultValue: ShipSchemaTankInput;
	configuration: TankConfiguration;
}

const calculateCenterTanksPosition = (tanks: ShipSchemaTankInput[]) => {
	const positionNumber = Math.max(
		...tanks.map(tank => tank.positionNumber || 0),
		0,
	);
	return positionNumber + 1;
};

const calculateStarboardPortPosition = (tanks: ShipSchemaTankInput[], position: TankPosition, numberingScheme: TankNumberingScheme) => {
	if (numberingScheme === TankNumberingScheme.Mirrored) {
		const filteredTanks = tanks.filter(t => t.position === position);

		const positionNumber = Math.max(
			...filteredTanks.map(tank => tank.positionNumber || 0),
			0,
		);

		return positionNumber + 1;
	} else {
		// In sequential numbering for port & starboard tanks, the numbering starts from the left (port)
		// So the port numbers are always odd, and the starboard numbers are even (Port 1, Starboard 2, Port 3, Starboard 4, etc.)
		const portTanks = tanks.filter(tank => tank.position === TankPosition.Port);
		const starboardTanks = tanks.filter(tank => tank.position === TankPosition.Starboard);

		if (position === TankPosition.Port) {
			return (portTanks.length + 1) * 2 - 1;
		} else {
			return (starboardTanks.length + 1) * 2;
		}
	}
};

const canDelete = (
	tank: ShipSchemaTankInput,
	tanks: ShipSchemaTankInput[],
	position: TankPosition,
	numberingScheme: TankNumberingScheme,
	configuration: TankConfiguration,
) => {
	// Only the last tank can be deleted to avoid complexities with having to recalculate the position each time
	let highestPositionNumber;
	if (configuration === TankConfiguration.PortStarboardTanks) {
		if (numberingScheme === TankNumberingScheme.Sequential) {
			const portAndStarboardTanks = tanks.filter(t => t.position === TankPosition.Port || t.position === TankPosition.Starboard);
			highestPositionNumber = Math.max(...portAndStarboardTanks.map(t => t.positionNumber || 0), 0);
			return tank.positionNumber === highestPositionNumber;
		} else {
			const portOrStarboardTanks = tanks.filter(t => t.position === position);
			highestPositionNumber = Math.max(...portOrStarboardTanks.map(t => t.positionNumber || 0), 0);
			return tank.positionNumber === highestPositionNumber;
		}
	} else {
		const centerTanks = tanks.filter(t => t.position === position);
		highestPositionNumber = Math.max(...centerTanks.map(t => t.positionNumber || 0), 0);
	}
	return tank.positionNumber === highestPositionNumber;
};

const ShipTanksForm = ({ ship, loading }: ShipTanksProps) => {

	const { control, setValue } = useFormContext<ShipSchemaInput, unknown, ShipSchemaOutput>();

	const { fields, remove, append } = useFieldArray({
		control,
		name: "tanks",
	});

	const tankProperties = useWatch({
		control,
		name: "tankProperties",
	});

	if (loading) {
		return <></>;
	}

	return (
		<Grid container spacing={3} padding={2}>
			<ShipTankPropertiesForm disabled={ship?.id != undefined && ship.tanks.length > 0} />

			{tankProperties?.tankConfiguration === TankConfiguration.CenterTanks ? (
				<ShipCenterTanks
					tanks={fields}
					position={TankPosition.Center}
					numberingScheme={TankNumberingScheme.Sequential}
					append={append}
					remove={remove} />
			) : tankProperties?.tankConfiguration === TankConfiguration.PortStarboardTanks ? (
				<>
					<ShipPortTanks
						tanks={fields}
						numberingScheme={TankNumberingScheme.Mirrored}
						position={TankPosition.Port}
						append={append}
						remove={remove} />

					<ShipStarboardTanks
						tanks={fields}
						numberingScheme={TankNumberingScheme.Mirrored}
						position={TankPosition.Starboard}
						append={append}
						remove={remove} />
				</>
			) : (
				<></>
			)}

			{fields.length > 0 && (
				<Grid size={12}>
					<Box sx={{ display: "flex", justifyContent: "end" }}>
						<Button onClick={() => setValue("tanks", [])} size="small" variant="outlined">{intl.formatMessage({ id: "SHIP_TANKS_REMOVE_ALL" })}</Button>
					</Box>
				</Grid>
			)}
		</Grid>
	);
};

const ShipCenterTanks = ({ tanks, position, numberingScheme, append, remove }: ShipTanksLayoutProps) => {
	const defaultCenterTank = {
		id: null,
		capacityM3: null,
		position: TankPosition.Center,
		positionNumber: calculateCenterTanksPosition(tanks),
	};

	return (
		<ShipTanksGeneral
			tanks={tanks}
			position={position}
			configuration={TankConfiguration.CenterTanks}
			numberingScheme={numberingScheme}
			append={append}
			remove={remove}
			defaultValue={defaultCenterTank} />
	);
};

const ShipPortTanks = ({ tanks, position, numberingScheme, append, remove }: ShipTanksLayoutProps) => {
	const defaultPortTank = {
		id: null,
		capacityM3: null,
		position: TankPosition.Port,
		positionNumber: calculateStarboardPortPosition(tanks, TankPosition.Port, numberingScheme),
	};

	return (
		<ShipTanksGeneral
			tanks={tanks}
			position={position}
			configuration={TankConfiguration.PortStarboardTanks}
			numberingScheme={numberingScheme}
			append={append}
			remove={remove}
			defaultValue={defaultPortTank} />
	);
};

const ShipStarboardTanks = ({ tanks, position, numberingScheme, append, remove }: ShipTanksLayoutProps) => {
	const defaultStarboardTank = {
		id: null,
		capacityM3: null,
		position: TankPosition.Starboard,
		positionNumber: calculateStarboardPortPosition(tanks, TankPosition.Starboard, numberingScheme),
	};

	return (
		<ShipTanksGeneral
			tanks={tanks}
			position={position}
			configuration={TankConfiguration.PortStarboardTanks}
			numberingScheme={numberingScheme}
			append={append}
			remove={remove}
			defaultValue={defaultStarboardTank} />
	);
};

const ShipTanksGeneral = ({ tanks, position, numberingScheme, configuration, append, remove, defaultValue }: ShipTanksGeneralProps) => {
	const { control } = useFormContext<ShipSchemaInput, unknown, ShipSchemaOutput>();

	return (
		<Grid size={12}>
			<Grid size={3}>
				<Tooltip title={intl.formatMessage({ id: `SHIP_TANKS_TOOLTIP_${position}` })}>
					<Box marginBottom={2} sx={{ display: "flex", alignItems: "center" }}>
						<InfoOutlinedIcon fontSize="small" color="primary" />
						<Typography
							marginLeft={1}
							color="primary"
							fontSize={14}
							fontWeight={"medium"}>
							{intl.formatMessage({ id: `SHIP_TANKS_${position}` })}
						</Typography>
					</Box>
				</Tooltip>
			</Grid>

			<FormArrayContainer onAdd={() => append(defaultValue)}>
				<Grid container spacing={2} sx={{ flexWrap: "nowrap", overflowX: "auto", paddingY: 1 }}>
					{tanks.map((item, index) => {
						if (item.position !== position) {
							return <></>;
						}
						return (
							<Grid key={item.id} sx={{ minWidth: 300 }}>
								<FormArrayRow
									collapsable={false}
									title={`${configuration === TankConfiguration.PortStarboardTanks ?
										(intl.formatMessage({ id: `TANK_${item.position}_PREFIX` })) : ""}
										${intl.formatMessage({ id: "TANK" })} #${item.positionNumber}`}
									onDelete={() => remove(index)}
									canDelete={canDelete(item, tanks, position, numberingScheme, configuration)}
									slotProps={{
										root: {
											variant: "elevation",
											square: false,
										},
									}}
								>
									<Grid container spacing={3} alignItems="center">
										<Grid size={12}>
											<FormGroup labelPlacement="left-center" label={intl.formatMessage({ id: "SHIPS_CAPACITY_M3" })} overflow="hidden">
												<Controller
													control={control}
													name={`tanks.${index}.capacityM3`}
													render={({ field }) => (
														<NumberInput
															{...field}
															decimalScale={2}
															addonAfter={intl.formatMessage({ id: "UNIT_M3" })}
														/>
													)}
												/>
											</FormGroup>
										</Grid>
									</Grid>
								</FormArrayRow>
							</Grid>
						);
					}).reverse()}
				</Grid>
			</FormArrayContainer>
		</Grid>
	);
};

export default ShipTanksForm;
